import { Sheet } from '../../../utils/download-utils';
import { CampaignBreakoutRow } from './data-qa-types';
import { ModelWithStages } from './hooks/useAllModelStages';
import { exportSheetName, modelSheetParts } from './model-export-utils';

export interface ExportWindow {
  startDate: string;
  endDate: string;
}

export const exportWindowKey = ({ startDate, endDate }: ExportWindow): string =>
  `${startDate}|${endDate}`;

// The date span a model's sheet covers: every stage's fencepost window combined (Brand
// Equity's two stages share one sheet). A stage with no declared fencepost falls back to
// its own data's date range. Undefined only for a model with no dates at all.
export const modelExportWindow = (model: ModelWithStages): ExportWindow | undefined => {
  const starts: string[] = [];
  const ends: string[] = [];
  model.stages.forEach(({ config, dataset }) => {
    const weekIsos = dataset.weeks.map(w => w.weekIso).sort((a, b) => a.localeCompare(b));
    const start = config.fencepostStart ?? weekIsos[0];
    const end = config.fencepostEnd ?? weekIsos[weekIsos.length - 1];
    if (start && end) {
      starts.push(start);
      ends.push(end);
    }
  });
  if (starts.length === 0) return undefined;
  return {
    startDate: starts.reduce((a, b) => (a < b ? a : b)),
    endDate: ends.reduce((a, b) => (a > b ? a : b))
  };
};

// The feature_ids a model actually uses, upper-cased for case-insensitive matching
// (featureMapping's comma-joined featureId strings, e.g. "FEATID251, FEATID252").
// Undefined means "don't filter": a model whose featurization was manually overridden
// (its featureMapping doesn't reflect what trained it) or that has no mapping data.
export const modelFeatureIds = (model: ModelWithStages): Set<string> | undefined => {
  if (model.stages.some(({ config }) => config.featureIdsOverridden)) return undefined;
  const ids = new Set<string>();
  model.stages.forEach(({ config }) =>
    config.featureMapping.forEach(row =>
      row.featureId
        .split(',')
        .map(id => id.trim().toUpperCase())
        .filter(Boolean)
        .forEach(id => ids.add(id))
    )
  );
  return ids.size > 0 ? ids : undefined;
};

// Column order follows the sample workbook the client teams asked for. Unfeaturized
// campaigns (no featureId) belong to no model's features, so they're kept on every
// model's sheet to keep the featurization gap visible.
export const buildCampaignBreakoutSheetRows = (
  rows: CampaignBreakoutRow[],
  featureIds: Set<string> | undefined
): Record<string, string | number>[] =>
  rows
    .filter(row => !featureIds || !row.featureId || featureIds.has(row.featureId.toUpperCase()))
    .map(row => ({
      Channel: row.channel,
      Platform: row.platform,
      'Campaign ID': row.campaignId,
      Campaign: row.campaignName,
      Feature: row.feature,
      'First Seen': row.firstSeen,
      Source: row.source,
      'Last Seen': row.lastSeen,
      Spend: row.spend,
      Impressions: row.impressions
    }));

// One sheet per model, "<company>_campaigns_<model>". `rowsByWindow` holds the
// company-wide rows fetched once per distinct window (see exportWindowKey).
export const buildCampaignBreakoutSheets = (
  company: string,
  models: ModelWithStages[],
  rowsByWindow: Map<string, CampaignBreakoutRow[]>
): Sheet[] => {
  const parts = modelSheetParts(models);
  return models.map((model, i) => {
    const exportWindow = modelExportWindow(model);
    const rows = exportWindow ? (rowsByWindow.get(exportWindowKey(exportWindow)) ?? []) : [];
    return {
      name: exportSheetName(company, 'campaigns', parts[i]),
      data: buildCampaignBreakoutSheetRows(rows, modelFeatureIds(model))
    };
  });
};
